import React, { useEffect, useState } from 'react';
import {
    Grid, Box, Typography, Stack, Chip, Divider,
    CircularProgress, Paper, Avatar, Card, Rating, FormControl, Select, MenuItem, TextField
} from '@mui/material';
import {
    CalendarMonth, AssignmentOutlined, Speed,
    CorporateFare, ForumOutlined, PriorityHigh
} from '@mui/icons-material';

function GoalDetailsPage({ goalData, isEditing, onSave }) {
    const [formData, setFormData] = useState(goalData);

    useEffect(() => {
        setFormData(goalData);
    }, [goalData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (!goalData) return <Typography>No goal data selected.</Typography>;

    const getStatusColor = (status) => {
        const config = {
            'Submitted For Review': 'warning',
            'Pending': 'error',
            'In Progress': 'info',
            'Completed': 'success'
        };
        return config[status] || 'default';
    };

    const getProgressColor = (progress) => {
        if (progress <= 25) return '#ef4444'; // Red
        if (progress <= 50) return '#f59e0b'; // Orange
        if (progress <= 75) return '#3b82f6'; // Blue
        return '#10b981'; // Green
    };

    const getPriorityColor = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high': return '#ef4444';
            case 'medium': return '#f59e0b';
            case 'low': return '#10b981';
            default: return '#64748b';
        }
    };

    return (
        <Box sx={{ py: 2 }}>
            {/* Header Section */}
            <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 4 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* Background Circle (The Track) */}
                    <CircularProgress
                        variant="determinate"
                        sx={{
                            color: (theme) => theme.palette.grey[200], // Default light gray for remaining value
                        }}
                        size={80}
                        thickness={5}
                        value={100} // Always 100% to fill the circle
                    />

                    {/* Foreground Circle (The Actual Progress) */}
                    <CircularProgress
                        variant="determinate"
                        value={formData?.progress || 0}
                        size={80}
                        thickness={5}
                        sx={{
                            color: getProgressColor(formData?.progress || 0),
                            position: 'absolute', // Position it exactly over the background circle
                            left: 0,
                            // Smooth out the line ends (optional)
                            [`& .MuiCircularProgress-circle`]: {
                                strokeLinecap: 'round',
                            },
                        }}
                    />

                    {/* Percentage Text */}
                    <Box
                        sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {`${formData?.progress || 0}%`}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" fontWeight="800" color="#1e293b">
                        {formData?.goalTitle}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                        {isEditing ? (
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={formData.status}
                                    onChange={(e) => handleChange('status', e.target.value)}
                                    sx={{ height: 32, fontSize: '0.8rem', fontWeight: 'bold' }}
                                >
                                    {['Pending', 'In Progress', 'Submitted For Review', 'Completed'].map(s => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Chip
                                label={formData.status}
                                size="small"
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem',
                                    bgcolor: getStatusColor(formData.status).bg,
                                    color: getStatusColor(formData.status).color,
                                    border: `1px solid ${getStatusColor(formData.status).border || '#cbd5e1'}`
                                }}
                            />
                        )}
                        <Chip
                            icon={<PriorityHigh sx={{ fontSize: '14px !important' }} />}
                            label={`Priority: ${goalData.priority || 'Medium'}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: '600', fontSize: '0.7rem', color: getPriorityColor(goalData.priority), borderColor: getPriorityColor(goalData.priority) }}
                        />
                    </Stack>
                </Box>
            </Stack>

            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1.5, color: '#64748b' }}>OWNERSHIP</Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: '#f8fafc',
                            flexGrow: 1, // Forces paper to take up all available vertical space
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                    {goalData.name[0]}
                                </Avatar>
                                <Box>
                                    <Typography variant="body2" fontWeight="bold">{goalData.name}</Typography>
                                    <Typography variant="caption" color="textSecondary">{goalData.position}</Typography>
                                </Box>
                            </Stack>
                            <Divider />
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CorporateFare sx={{ color: 'gray', fontSize: 18 }} />
                                <Typography variant="caption"><strong>Department:</strong> {goalData.department}</Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Section 2: Metrics */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1.5, color: '#64748b' }}>METRICS & TIMELINE</Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: '12px',
                            flexGrow: 1, // Forces paper to take up all available vertical space
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1.5}>
                                <CalendarMonth sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="caption" color="textSecondary" display="block">DEADLINE</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {new Date(goalData.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1.5}>
                                <Speed sx={{ color: 'secondary.main', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="caption" color="textSecondary" display="block">KPI / MEASURABLE TARGET</Typography>
                                    <Typography variant="body2" fontWeight="bold">{goalData.target}</Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Section 3: Performance Rating */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1.5, color: '#64748b' }}>PERFORMANCE RATING</Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: '12px',
                            flexGrow: 1, // Forces paper to take up all available vertical space
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography variant="caption" color="textSecondary" gutterBottom>OVERALL SCORE</Typography>
                        {isEditing ? (
                            <Box>
                                <Typography variant="caption" color="textSecondary">Edit Overall Score (1-5)</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    inputProps={{
                                        step: 0.1,
                                        min: 1,
                                        max: 5
                                    }}
                                    value={formData.overallRating || ''}
                                    onChange={(e) => {
                                        let val = parseFloat(e.target.value);
                                        if (!isNaN(val)) {
                                            if (val > 5) val = 5;
                                            if (val < 1) val = 1;
                                        }

                                        handleChange('overallRating', val);
                                        if (!isNaN(val)) {
                                            const progressVal = Math.round(((val - 1) / 4) * 100);
                                            handleChange('progress', Math.max(0, progressVal));
                                        }
                                    }}
                                />
                            </Box>
                        ) : (
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="h4" fontWeight="800" color="primary.main">{formData.overallRating || "0.0"}</Typography>
                                <Box>
                                    <Rating
                                        value={goalData.overallRating || 0}
                                        precision={0.5}
                                        readOnly
                                        sx={{ color: '#F59E0B' }}
                                    />
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Based on current progress
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </Paper>
                </Grid>

                {/* Section 3: Description & Strategy */}
                <Grid size={{ xs: 12 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, mt: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', gap: 1, alignItems: 'center' }}>
                            <AssignmentOutlined sx={{ color: '#64748b' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Description & Strategy
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                            {goalData.description || `The primary objective of this goal is to focus on ${goalData.goalTitle.toLowerCase()} within the ${goalData.department} department. Progress is currently tracked against quality standards set for H1-2026.`}
                        </Box>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, mt: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', gap: 1, alignItems: 'center' }}>
                            <ForumOutlined sx={{ color: '#64748b' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Feedback
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                            {isEditing ? (
                                <TextField
                                    fullWidth multiline rows={3}
                                    placeholder="Enter manager feedback..."
                                    value={formData.feedback || ''}
                                    onChange={(e) => handleChange('feedback', e.target.value)}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {formData.feedback || "No feedback provided yet."}
                                </Typography>
                            )}
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default GoalDetailsPage;